import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import {
  ButtonPrimary,
  IconCheck,
  IconSearch,
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
} from '@spotify-internal/encore-web';
import { ErrorBoundary } from '../../../features/src/Platform';
import { useT } from '@spotify-internal/i18n-core';
import { PageHead } from '../../features/Layout';

const Container = styled.div`
  height: 100%;
`;

const StyledEmptyStateTitle = styled(EmptyStateTitle)`
  margin-bottom: 48px;
`;

const ErrorFallbackPage = () => {
  const t = useT();

  return (
    <EmptyState icon={IconSearch}>
      <EmptyStateTitle>
        {t(
          'CONTINUED_AUTH_ERROR_TITLE',
          "We couldn't find that page.",
          'Error title for when something goes wrong on the Continued Authentication page',
        )}
      </EmptyStateTitle>
      <EmptyStateText>
        {t(
          'CONTINUED_AUTH_ERROR_TEXT',
          "The link may be broken or we've taken the page down.",
          'Error text for when something goes wrong on the Continued Authentication page',
        )}
      </EmptyStateText>
    </EmptyState>
  );
};

const Page = () => {
  const t = useT();
  const router = useRouter();
  const query = router.query;

  useEffect(() => {
    if (!Object.keys(query).length) {
      throw new Error(
        'Query parameters expected in order to properly authenticate.',
      );
    }
  }, [query]);

  const onContinueToS4A = () => {
    const baseUrl = 'spotifyartists://continue-authentication';
    const queryString = Object.keys(query)
      .map(key => `${key}=${query[key]}`)
      .join('&');

    window.location.href = `${baseUrl}?${queryString}`;
  };

  return (
    <EmptyState icon={IconCheck} iconColorSet="positive">
      <StyledEmptyStateTitle>
        {t(
          'CONTINUED_AUTH_TITLE',
          "You're logged in",
          'Title for continued authentication page letting users know they have logged into the app',
        )}
      </StyledEmptyStateTitle>
      <ButtonPrimary onClick={() => onContinueToS4A()}>
        {t(
          'CONTINUED_AUTH_CTA',
          'Open Spotify for Artists',
          'Call to action button to continue authenticating into the mobile app',
        )}
      </ButtonPrimary>
    </EmptyState>
  );
};

const ContinueAuthentication = () => {
  const t = useT();

  return (
    <Container>
      <PageHead
        title={t(
          'CONTINUED_AUTH_PAGE_TITLE',
          'Continue Authentication',
          'Page title for continued authentication page',
        )}
      />
      <ErrorBoundary fallback={<ErrorFallbackPage />}>
        <Page />
      </ErrorBoundary>
    </Container>
  );
};

export default ContinueAuthentication;
